import { call } from '@advanza/api'
import { useEffect, useState } from 'react'

export const formatNativeAppSeconds = (s: number | null): string => {
    if (s === null) return '–'
    if (s < 60) return `${Math.round(s)}s`
    if (s < 3600) {
        let m = Math.floor(s / 60)
        let sec = Math.round(s - m * 60)
        if (sec === 60) {
            sec = 0
            m += 1
        }
        return sec > 0 ? `${m}m ${sec}s` : `${m}m`
    }
    let h = Math.floor(s / 3600)
    let m = Math.round((s - h * 3600) / 60)
    if (m === 60) {
        m = 0
        h += 1
    }
    return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export const formatNativeAppMinutes = (m: number | null): string => {
    if (m === null) return '–'
    if (m < 1) return '< 1m'
    if (m < 60) return `${Math.round(m)}m`
    let h = Math.floor(m / 60)
    let min = Math.round(m - h * 60)
    if (min === 60) {
        min = 0
        h += 1
    }
    return min > 0 ? `${h}h ${min}m` : `${h}h`
}

export const formatNativeAppDate = (iso: string, includeTime = false): string => {
    try {
        return new Intl.DateTimeFormat('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        }).format(new Date(iso))
    } catch {
        return iso
    }
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

export const formatNativeAppRelative = (iso: string, now: Date = new Date()): string => {
    try {
        const then = new Date(iso).getTime()
        if (Number.isNaN(then)) return ''
        const diffMs = now.getTime() - then
        if (diffMs < 0) return 'in the future'
        if (diffMs < MINUTE_MS) return 'just now'
        if (diffMs < HOUR_MS) {
            const m = Math.floor(diffMs / MINUTE_MS)
            return `${m}m ago`
        }
        if (diffMs < DAY_MS) {
            const h = Math.floor(diffMs / HOUR_MS)
            return `${h}h ago`
        }
        const d = Math.floor(diffMs / DAY_MS)
        if (d === 1) return '1 day ago'
        if (d < 30) return `${d} days ago`
        if (d < 365) {
            const months = Math.floor(d / 30)
            return months === 1 ? '1 month ago' : `${months} months ago`
        }
        const years = Math.floor(d / 365)
        return years === 1 ? '1 year ago' : `${years} years ago`
    } catch {
        return ''
    }
}

export interface NativeAppDevice {
    platform: string
    device_name: string
    registered_at: string
}

export interface NativeAppActivity {
    active_24h: boolean
    active_7d: boolean
    active_30d: boolean
    last_active: string | null
}

export interface NativeAppNotifications {
    sent: number
    shown: number
    clicked: number
    open_rate: number
    avg_open_time_seconds: number | null
}

export interface NativeAppResponseTime {
    avg_response_time_minutes: number | null
}

export interface NativeAppScreenView {
    screen_name: string
    view_count: number
}

export interface NativeAppRecentEvent {
    event_type: string
    event_data: Record<string, unknown> | null
    created: string
}

export interface NativeAppBehavioral {
    top_screens: NativeAppScreenView[]
    recent_events: NativeAppRecentEvent[]
}

export interface NativeAppProviderStats {
    has_native_app: boolean
    push_notifications_enabled: boolean
    dormant?: boolean
    days_inactive?: number | null
    paused?: boolean
    pause_reason?: string | null
    paused_until?: string | null
    devices?: NativeAppDevice[]
    activity?: NativeAppActivity
    notifications?: NativeAppNotifications
    response_time?: NativeAppResponseTime
    behavioral?: NativeAppBehavioral
}

const CACHE_TTL_MS = 5 * 60 * 1000

interface CacheEntry {
    promise: Promise<NativeAppProviderStats | null>
    value?: NativeAppProviderStats
    expiresAt?: number
}

const cache = new Map<string, CacheEntry>()

const hasFreshValue = (
    entry: CacheEntry | undefined,
): entry is CacheEntry & {
    value: NativeAppProviderStats
    expiresAt: number
} =>
    entry !== undefined &&
    entry.value !== undefined &&
    entry.expiresAt !== undefined &&
    entry.expiresAt > Date.now()

const fetchStats = (providerId: number | string): Promise<NativeAppProviderStats | null> =>
    // The `_` cache-buster keeps the URL unique so no CDN/browser cache can serve
    // a stale body; client-side freshness is handled by this hook's own TTL cache.
    call(`office/misc/native-app-provider-stats?provider_id=${providerId}&_=${Date.now()}`)
        .then((response) =>
            response?.status === 'success' ? (response as unknown as NativeAppProviderStats) : null,
        )
        .catch(() => null)

/**
 * Shared fetcher for the per-provider native app report. Memoizes the in-flight
 * promise per provider so both the compact Native app card and the full stats
 * block can render from a single backend call. Successful responses are cached
 * for CACHE_TTL_MS; transient failures are not memoized so the next mount can
 * retry.
 */
export const useProviderNativeAppStats = (providerId: number | string) => {
    const key = String(providerId)
    const [loading, setLoading] = useState(() => !hasFreshValue(cache.get(key)))
    const [report, setReport] = useState<NativeAppProviderStats | null>(() => {
        const existing = cache.get(key)
        return hasFreshValue(existing) ? existing.value : null
    })

    useEffect(() => {
        let cancelled = false
        let entry = cache.get(key)

        if (entry !== undefined && entry.value !== undefined && !hasFreshValue(entry)) {
            cache.delete(key)
            entry = undefined
        }

        if (entry === undefined) {
            entry = { promise: fetchStats(providerId) }
            cache.set(key, entry)
            entry.promise.then((value) => {
                const cached = cache.get(key)
                if (cached === undefined) return
                if (value === null) {
                    cache.delete(key)
                    return
                }
                cached.value = value
                cached.expiresAt = Date.now() + CACHE_TTL_MS
            })
        }

        if (hasFreshValue(entry)) {
            setReport(entry.value)
            setLoading(false)

            return () => {
                cancelled = true
            }
        }

        setLoading(true)
        entry.promise.then((value) => {
            if (cancelled) return
            setReport(value)
            setLoading(false)
        })

        return () => {
            cancelled = true
        }
    }, [key, providerId])

    return { loading, report }
}
